import {
  getEntitiesByFpIdDataOptions,
  getEntitiesByFpIdDocumentsOptions,
  getEntitiesByFpIdOnboardingsByOnboardingIdMatchSignalsOptions,
  getEntitiesByFpIdOnboardingsByOnboardingIdRiskSignalsOptions,
  getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions,
  getEntitiesByFpIdRuleSetResultByRuleSetResultIdOptions,
} from '@onefootprint/fetch-dashboard';
import type { Entity, EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { SETTLED_ONBOARDING_CACHE } from '@/entity/constants';
import { getEntityDuplicateDataOptions } from '@/entity/hooks/use-entity-duplicate-data';

/** The user hasn't finished or the checks are still running, so the results can change without a mutation here. */
export const isAwaitingResults = (onboarding: EntityOnboarding) =>
  !onboarding.deactivated && (onboarding.status === 'incomplete' || onboarding.status === 'pending');

/**
 * The queries behind one onboarding's panel. Every observer in the panel takes its options from here, because TanStack
 * judges staleness per observer as it mounts: one left at staleTime 0 would refetch what another just loaded.
 */
export const getOnboardingQueries = (entity: Pick<Entity, 'id' | 'kind'>, onboarding: EntityOnboarding) => {
  const fpId = entity.id;
  const path = { fpId, onboardingId: onboarding.id };
  // Always pass `query`, even without a seqno: the key without it is the entity-level entry.
  const query = { seqno: onboarding.seqno };
  const isPerson = entity.kind === 'person';
  const ruleSetResultId = onboarding.ruleSetResults[0]?.id ?? '';
  const cache = isAwaitingResults(onboarding)
    ? { staleTime: 0, gcTime: SETTLED_ONBOARDING_CACHE.gcTime }
    : SETTLED_ONBOARDING_CACHE;

  return {
    attributes: { ...getEntitiesByFpIdDataOptions({ path: { fpId }, query }), ...cache },
    documents: { ...getEntitiesByFpIdDocumentsOptions({ path: { fpId }, query }), ...cache },
    riskSignals: { ...getEntitiesByFpIdOnboardingsByOnboardingIdRiskSignalsOptions({ path }), ...cache },
    matchSignals: {
      ...getEntitiesByFpIdOnboardingsByOnboardingIdMatchSignalsOptions({ path }),
      ...cache,
      enabled: isPerson,
    },
    trace: {
      ...getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions({ path }),
      ...cache,
      // Without a graph the API answers 400.
      enabled: onboarding.hasGraphTrace,
    },
    ruleSetResult: {
      ...getEntitiesByFpIdRuleSetResultByRuleSetResultIdOptions({ path: { fpId, ruleSetResultId } }),
      ...cache,
      // A rule set result records one evaluation and never changes.
      staleTime: Number.POSITIVE_INFINITY,
      enabled: !onboarding.hasGraphTrace && Boolean(ruleSetResultId),
    },
    dupes: { ...getEntityDuplicateDataOptions(fpId, onboarding.completedAt), enabled: isPerson },
  };
};
